import { TempLayout } from "@/layouts"
import HeTimesheetCard from "./components/HeTimesheetCard"
import { SearchInput } from "@/components/ui/search-input"
import EmptyState from "@/components/common/EmptyState"
import { useState, useMemo, useEffect, useRef } from "react"
import TitleSection from "@/components/common/TitleSection"
import { useNavigate } from "react-router"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { useHeTimesheetStore } from "@/stores/he-timesheets/heTimesheetStore"
import { IHeTimesheet, IPaginationParam } from "@/types"
import { Skeleton } from "@/components/ui/skeleton"
import { useUserStore } from "@/stores"
import HeTimesheetsSpv from "./components/HeTimesheetsSpv"

const HeTimesheets = () => {
  const navigate = useNavigate()
  const { userData } = useUserStore()
  const isAdmin = userData?.roles?.includes("ROLE_ADMIN")
  const { heTimesheets, fetchHeTimesheets, isLoading, meta } = useHeTimesheetStore()
  const [searchQuery, setSearchQuery] = useState("")
  const [statusFilter, setStatusFilter] = useState<string>("all")
  
  const [allTimesheets, setAllTimesheets] = useState<IHeTimesheet[]>([])
  const prevDataLengthRef = useRef(0)
  const [params, setParams] = useState<IPaginationParam>({
    page: 1,
    per_page: 10,
  })

  useEffect(() => {
    fetchHeTimesheets(params).catch(() => {})
  }, [fetchHeTimesheets, params])
  useEffect(() => {
    if (heTimesheets && heTimesheets.length > 0) {
      const mappedNewData: IHeTimesheet[] = heTimesheets

      if (params.page === 1) {
        setAllTimesheets(mappedNewData)
        prevDataLengthRef.current = mappedNewData.length
      } else {
        setAllTimesheets(prev => {
           const existingIds = new Set(prev.map(t => t.record_id))
           const uniqueNewData = mappedNewData.filter(t => !existingIds.has(t.record_id))
           return [...prev, ...uniqueNewData]
        })
        prevDataLengthRef.current = heTimesheets.length
      }
    } else if (params.page === 1 && heTimesheets?.length === 0) {
       setAllTimesheets([])
    }
  }, [heTimesheets, params.page])

  const filteredTimesheets = useMemo(() => {
    let filtered = allTimesheets

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(timesheet => timesheet.status === statusFilter)
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase()
      filtered = filtered.filter(
        timesheet =>
          timesheet.vendor_name?.toLowerCase().includes(query) ||
          timesheet.hull_code?.toLowerCase().includes(query) ||
          timesheet.record_id?.toLowerCase().includes(query) ||
          timesheet.hull_name?.toLowerCase().includes(query)
      )
    }

    return filtered
  }, [searchQuery, statusFilter, allTimesheets])

  const handleLoadMore = () => {
    if (meta && params.page && params.page < meta.total_pages && !isLoading) {
      setParams(prev => ({ ...prev, page: (prev.page || 1) + 1 }))
    }
  }

  const handleRefetch = () => {
    setParams(prev => ({ ...prev, page: 1 }))
    void fetchHeTimesheets({ ...params, page: 1 })
  }

  const handleClearSearch = () => {
    setSearchQuery("")
  }

  const handleClearFilters = () => {
    setSearchQuery("")
    setStatusFilter("all")
  }

  if (isAdmin) {
    return <HeTimesheetsSpv />
  }

  return (
    <TempLayout title="Heavy Equipment Timesheet" navNavigateUrl="/home">
      <TitleSection title="HE Timesheet" />

      {/* Filters */}
      <div className="flex md:flex-row flex-col justify-between gap-3 mb-4">
        <SearchInput
          placeholder="Search by vendor, equipment code, or record ID..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          onClear={handleClearSearch}
        />

        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="md:w-[200px] w-full">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="DRAFT">Draft</SelectItem>
            <SelectItem value="POSTED">Posted</SelectItem>
            <SelectItem value="LOCKED">Locked</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading && params.page === 1 && allTimesheets.length === 0 ? (
        <div className="grid grid-cols-1 gap-4">
          {Array.from({ length: 6 }).map((_, index) => (
            <Skeleton key={index} className="h-40 w-full rounded-xl" />
          ))}
        </div>
      ) : filteredTimesheets.length === 0 && !isLoading ? (
        <EmptyState
          title="No Timesheets Found"
          actionLabel={searchQuery || statusFilter !== "all" ? "Clear Filters" : undefined}
          onAction={searchQuery || statusFilter !== "all" ? handleClearFilters : undefined}
        />
      ) : (
        <HeTimesheetCard 
          timesheets={filteredTimesheets} 
          onLoadMore={handleLoadMore} 
          hasMore={meta ? (params.page || 1) < meta.total_pages : false} 
          isLoading={isLoading} 
        />
      )}
    </TempLayout>
  )
}

export default HeTimesheets
